import {
  monthly_textAreaQuestions,
  monthlyCheckboxQuestions,
} from "./form-data/monthlyFeedback-data";
import CheckboxGroupInput from "./common-components/CheckboxInput";
import InlineTextInput from "./common-components/InlineTextInput";
import BreadCrumbForm from "./common-components/BreadCrumbForm";
import TextareaInput from "./common-components/TextAreaInput";
import { useEffect, useState } from "react";
import InlineDateField from "./common-components/InlineDateField";
import useApi from "../../core/customhook/useApi";
import Swal from "sweetalert2";
import { MonthlyFormErrorProps, validateMonthlyForm } from "./validation/form-validations/Monthly-validation";
import { all_routes } from "../router/all_routes";
import { useNavigate, useParams } from "react-router";
import { GET_REGISTERED_EMP_REQUIRED_DATA, GET_MONTHLY_BY_EMPID, save_monthly_feedback_form } from "../../environment";
import FormHeading from "./common-components/FormHeading";
import Loading from "../../core/common/custom-loading/Loading";
import { formatSlashDate } from "../../core/common/formattedDate";
import ESignaturePad from "../../core/common/ESignaturePad";
import ViewSignature from "../../core/common/ViewSignature";
import { toast } from "react-toastify";
import dayjs from "dayjs";

export type MonthlyFormProps = {
  name: string;
  dateOfJoining: string;
  departmentName: string;
  supportAndResources: string;
  collaborationOpportunities: string;
  wellPreparedForRole: string;
  furtherClarificationNeeded: string;
  comfortableApproachingOthers: string;
  receivedWeConnectCredentials: string;
  acceptedWeConnectPolicies: string;
  regularlyFillingTimesheet: string;
  goalsSubmittedOnPromptHR: string;
  onboardingFeedback: string;
  overallRating: string;
  signatureDate?: string;
  signature_url?: string;
  signature?: File | null;

  month_number?: string;
};

const initialFormData = {
  name: "",
  dateOfJoining: "",
  departmentName: "",
  supportAndResources: "",
  collaborationOpportunities: "",
  wellPreparedForRole: "",
  furtherClarificationNeeded: "",
  comfortableApproachingOthers: "",
  receivedWeConnectCredentials: "",
  acceptedWeConnectPolicies: "",
  regularlyFillingTimesheet: "",
  goalsSubmittedOnPromptHR: "",
  onboardingFeedback: "",
  overallRating: "",
  signatureDate: "",
  signature: null,

  month_number: "",
};

const MonthlyOnboardingFeedbackForm = () => {
  const { id: formId, employee_id } = useParams();
  const [formData, setFormData] = useState<MonthlyFormProps>(initialFormData);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);
  const [isAlreadySaved, setIsAlreadySaved] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  // --- api ---
  const {
    callApi: saveMonthlyAPI,
    loading: submitLoading,
    error,
  } = useApi(save_monthly_feedback_form, {
    method: "POST",
    autoCall: false,
  });
  const { callApi: fetchMonthlyDataAPI, loading: fetchLoading } = useApi("", {
    method: "GET",
    autoCall: false,
  });
  const { callApi: fetchEmployeePreData, loading: getEmpLoading } = useApi(
    GET_REGISTERED_EMP_REQUIRED_DATA,
    {
      method: "GET",
      autoCall: false,
    },
  );
  // --- / api ---

  const handleClearError = (name: keyof MonthlyFormProps) =>
    setErrors((prev: any) => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev: MonthlyFormProps) => ({
      ...prev,
      [name]: value,
    }));
    handleClearError(name as keyof MonthlyFormProps);
  };

  const handleDateChange = (name: string, value: string) => {
    const [day, month, year] = value.split("-");
    const formatted = `${year}-${month}-${day}`;
    setFormData((prev) => ({ ...prev, [name]: formatted }));
    handleClearError(name as keyof MonthlyFormProps);
  };

  const handleFieldDataChange = (name: string, value: string) => {
    setFormData((prev: MonthlyFormProps) => ({
      ...prev,
      [name]: prev[name as keyof MonthlyFormProps] == value ? "" : value,
    }));
    handleClearError(name as keyof MonthlyFormProps);

  };

  const handleSignatureChange = (
    field: keyof MonthlyFormProps,
    file: File | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
    handleClearError(field);
  };


  // --- handle form submit logic ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formId || !employee_id) return;

    const { errors, firstError } = validateMonthlyForm(formData);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.error("Please fill all required fields")
      return;
    }


    const payload = {
      employee_id: employee_id ?? "",
      name: employeeDetails?.employee_name ?? "",
      date_of_joining: dayjs(employeeDetails?.join_date, "DD/MM/YYYY").format("YYYY-MM-DD") ?? "",
      department: employeeDetails?.department ?? "",
      received_support_and_resources: formData?.supportAndResources ?? "",
      had_collaboration_opportunities:
        formData?.collaborationOpportunities ?? "",
      feel_well_prepared: formData?.wellPreparedForRole ?? "",
      areas_need_clarification: formData?.furtherClarificationNeeded ?? "",
      comfortable_approaching_colleagues:
        formData?.comfortableApproachingOthers ?? "",
      received_weconnect_credentials:
        formData?.receivedWeConnectCredentials ?? "",
      accepted_weconnect_policies: formData?.acceptedWeConnectPolicies ?? "",
      filling_timesheet_regularly: formData?.regularlyFillingTimesheet ?? "",
      goals_submitted_on_prompthr: formData?.goalsSubmittedOnPromptHR ?? "",
      onboarding_feedback_suggestions: formData?.onboardingFeedback ?? "",
      overall_rating: Number(formData?.overallRating) ?? "",
      signature_date: formData?.signatureDate ?? "",
      month_number: formData?.month_number ?? "",
      signature: formData?.signature ?? ""
    };

    const formDataPayload = new FormData()

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "signature" && value) {
        formDataPayload.append(key, value as File);
      } else if (value !== null && value !== undefined) {
        formDataPayload.append(key, String(value));
      }
    });

    // fetchData();

    try {
      const response = await saveMonthlyAPI(formDataPayload);
      if (response?.success) {
        // navigate(`${all_routes.managerEnabledMonthlyAssessment}`, {
        //   replace: true,
        // });
        // toast.success(response?.message || "Form Submitted Successfully")
        fetchData();
        Swal.fire({
          icon: "success",
          title: "Submitted!",
          text: response?.message || "Form submitted successfully",
          confirmButtonText: "OK",
          confirmButtonColor: "#fd7e14",
        });
        fetchData()
        // setFormData(initialFormData);
      }
    } catch (error: any) {
      console.error("error message", error.response);
      const errorMsg = error.response?.data?.message ?? null;
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: errorMsg ?? "Failed to submit form data.",
        confirmButtonText: "OK",
        confirmButtonColor: "#fd7e14",
      });
    }
  };

  useEffect(() => {
    if (!employee_id) return;
    const fetchEmpData = async () => {
      try {
        const res = await fetchEmployeePreData({
          emp_id: employee_id,
        });
        if (res?.success) {
          const resData = res?.data?.[0] || {};
          setEmployeeDetails(resData)
        }
      } catch (error) {
        console.error("failed to fetch:", error);
      }
    };
    fetchEmpData();
  }, [employee_id]);
  // --- /handle form submit logic ---

  // --- fetch details only if we have 'emp_id' ---
  const fetchData = async () => {
    try {
      const res = await fetchMonthlyDataAPI(
        undefined,
        `${GET_MONTHLY_BY_EMPID}/${formId}`,
      );
      if (res?.success) {
        const resData = res?.data || null;

        setFormData((prev) => ({
          ...prev,
          month_number: String(res?.month_number) ?? "",
        }))
        // console.log(resData)
        if (!resData) {
          setIsAlreadySaved(false);
          return;
        }
        setIsAlreadySaved(true)
        setFormData((prev) => ({
          ...prev,
          name: resData?.name ?? "",
          dateOfJoining: resData?.date_of_joining ?? "",
          departmentName: resData?.department ?? "",
          supportAndResources: resData?.received_support_and_resources ?? "",
          collaborationOpportunities: resData?.had_collaboration_opportunities ?? "",
          wellPreparedForRole: resData?.feel_well_prepared ?? "",
          furtherClarificationNeeded: resData?.areas_need_clarification ?? "",
          comfortableApproachingOthers:
            resData?.comfortable_approaching_colleagues ?? "",
          receivedWeConnectCredentials: resData?.received_weconnect_credentials ?? "",
          acceptedWeConnectPolicies: resData?.accepted_weconnect_policies ?? "",
          regularlyFillingTimesheet: resData?.filling_timesheet_regularly ?? "",
          goalsSubmittedOnPromptHR: resData?.goals_submitted_on_prompthr ?? "",
          onboardingFeedback: resData?.onboarding_feedback_suggestions ?? "",
          overallRating: resData?.overall_rating ?? "",
          signatureDate: resData?.signature_date ?? "",
          signature_url: resData?.signature ?? "",
        }));
      }
    } catch (error) {
      console.error("failed to fetch:", error);
    }
  };
  useEffect(() => {
    if (!formId) return;
    fetchData();
    setFormData((prev) => ({ ...prev, signatureDate: dayjs().format("YYYY-MM-DD") }))
  }, [formId]);

  // const isFieldsNonEditable: boolean = isAlreadySaved;
  const isFieldsNonEditable = isAlreadySaved;
  // employee_id && formData?. > 0 ? true : false;

  // console.log("errors", errors)
  // console.log('formdata:', formData)

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <BreadCrumbForm
          title="Monthly Onboarding Feedback Form"
          linkTitle="Monthly Feedback Form"
          hasBackButton={true}
          backLink={`${all_routes.managerEnabledMonthlyAssessment}`}
        />

        {/* Form content */}
        <div className="card shadow-sm mt-4 form-card-container">
          <div className="card-body p-4 p-md-5">

            {
              getEmpLoading || fetchLoading ? (
                <Loading />
              ) :
                (
                  <>
                    <FormHeading title="Monthly Onboarding Feedback Form" />
                    <form onSubmit={handleSubmit}>
                      {/* Basic Info */}
                      <div>
                        <InlineTextInput
                          label={"Employee Name:"}
                          name={"name"}
                          value={employeeDetails?.employee_name ?? ""}
                          isReadOnly={true}
                          classStyle="text-capitalize"
                        // onChange={handleChange}
                        // disabled
                        />
                        <InlineTextInput
                          label={"Date of Joining:"}
                          name={"dateOfJoining"}
                          value={formatSlashDate(employeeDetails?.join_date) ?? ""}
                          isReadOnly={true}
                        // onChange={handleChange}
                        // disabled
                        />
                        {/* <InlineDateField
                      label="Date of Joining:"
                      name="dateOfJoining"
                      value={formData?.dateOfJoining ?? ""}
                      disabled
                      onChange={handleDateChange}
                      errors={errors}
                    /> */}
                        <InlineTextInput
                          label="Department:"
                          name="departmentName"
                          value={employeeDetails?.department ?? ""}
                          // onChange={handleChange}
                          errors={errors}
                          // disabled={true}
                          isReadOnly={true}
                        />
                      </div>
                      <hr />

                      <div className="mt-4">
                        {monthlyCheckboxQuestions?.map((ques) => {
                          return (
                            <CheckboxGroupInput
                              label={ques?.label}
                              name={ques?.name}
                              options={ques?.options}
                              value={
                                formData[ques.name as keyof Omit<MonthlyFormErrorProps, 'signature'>] ?? ""
                              }
                              onChange={handleFieldDataChange}
                              errors={errors}
                              isDisabled={isFieldsNonEditable}
                            />
                          );
                        })}
                        <hr />

                        {/* Open-ended */}
                        {monthly_textAreaQuestions?.map((ques) => {
                          return (
                            <TextareaInput
                              label={ques.label}
                              name={ques.name}
                              rows={ques.rows}
                              value={
                                formData[ques.name as keyof Omit<MonthlyFormErrorProps, 'signature'>] ?? ""
                              }
                              onChange={handleChange}
                              errors={errors}
                              isReadOnly={isFieldsNonEditable}
                            />
                          );
                        })}
                        <div className="mb-4 form-dark-text">
                          <label className="form-label ">
                            Overall Rating out of 5 (5 is the highest)
                          </label>
                          <div className="d-flex gap-4 flex-wrap">
                            {["1", "2", "3", "4", "5"]?.map((opt) => {
                              return (
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name={"overallRating"}
                                    id={`overallRating-${opt}`}
                                    value={opt}
                                    checked={formData.overallRating == opt}
                                    onChange={(e) =>
                                      handleFieldDataChange(
                                        e.target.name,
                                        e.target.value,
                                      )
                                    }
                                    disabled={isFieldsNonEditable}
                                  />
                                  <label
                                    className="form-check-label ps-2"
                                    htmlFor={`overallRating-${opt}`}
                                  >
                                    {opt}
                                  </label>
                                </div>
                              );
                            })}
                          </div>

                          {errors["overallRating"]?.length > 0 && (
                            <p className="flex-1 text-danger mt-1 ps-1 fs-12 ">
                              {errors["overallRating"]}
                            </p>
                          )}
                        </div>

                        {/* Signature */}
                        <div className="mt-5 pb-5">
                          {/* Signature */}
                          <div className="mb-3">
                            {/* {!isAlreadySaved ? <ViewSignature fileSource={typeof formData?.signature == 'string' ? formData?.signature : ""} /> : <> */}
                            <label htmlFor="signature" className="form-label fw-semibold">
                              Signature of Employee:
                            </label>
                            {isAlreadySaved ? <ViewSignature fileSource={formData?.signature_url ?? ""} /> :
                              <ESignaturePad
                                field="signature"
                                onChange={handleSignatureChange}
                                existingSignatureUrl={
                                  typeof formData.signature === "string"
                                    ? formData.signature // URL string from API (edit mode)
                                    : null
                                }
                                error={errors.signature}
                                isDisabled={submitLoading}
                              />
                            }
                          </div>
                          <InlineDateField
                            label="Date:"
                            name="signatureDate"
                            value={formData?.signatureDate}
                            onChange={handleDateChange}
                            disabled={true}
                          // errors={errors}
                          />
                        </div>
                      </div>

                      <div className="text-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitLoading || isFieldsNonEditable}
                        // disabled={Object.keys(errors)?.length > 0 || loading}
                        >
                          {submitLoading ? "Submitting..." : "Submit"}
                        </button>
                      </div>
                    </form>
                  </>
                )}
          </div>
          {/* Form content */}
        </div>

        <div className="footer d-flex align-items-center justify-content-center   border-top bg-white p-3">
          <p className="mb-0"> © {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default MonthlyOnboardingFeedbackForm;

// useEffect(() => {
//   if (!newJoinerDataById) return;
//   setFormData((prev: MonthlyFormProps) => ({
//     ...prev,
//     name: newJoinerDataById?.full_name,
//     dateOfJoining: newJoinerDataById?.date_of_joining,
//     departmentName: newJoinerDataById?.department,
//   }));
// }, [newJoinerDataById]);

// useEffect(() => {
//   if (!id) return;
//   setNewJoinerId(id);
// }, [id, onboarding_id]);
// --- /set form data ---

// useEffect(() => {
//   if (!id || !onboarding_id) {
//     navigate(all_routes.managerEnabledMonthlyAssessment, { replace: true });
//   }
// }, [id, onboarding_id]);

{
  /* <h4 className="text-center fw-bold text-decoration-underlin mb-5">
                  Monthly Onboarding Feedback Form
                </h4> */
}
